import React, { useEffect, useState } from "react";
import { ACCENT } from "../artistStudio/shared";
import { parentalApi } from "./parentalApi";

// ---------------------------------------------------------------------------------------------------------------------------------
// Parental Control 13+, sheet row 1.3.1: "Parent-managed spending allowance".
//
// This panel is shown to a PARENT, under one linked teen on the Family page (next to the time limits). It does three things:
//   1. shows how much the teen has spent this calendar month and how much of the allowance is left,
//   2. lets the parent set the monthly allowance (a dollar amount, "no spending" or "no limit"),
//   3. lists the payments counted this month, so the parent can see what the money went on.
//
// The money rules are NOT decided here. The server (parental-allowance.service.js) does the counting and refuses any payment that
// would go over the allowance, so this page only reads and sets the number. The teen's own view of the allowance is the small card in
// FamilyLink.js (TeenView); the teen cannot change it.
// ---------------------------------------------------------------------------------------------------------------------------------

// The three ways an allowance can be set. Keeping them as named modes makes the form easy to read and to test:
//   "amount"  - the teen may spend up to a dollar amount each calendar month
//   "none"    - the teen may not spend anything (server value 0)
//   "nolimit" - there is no allowance at all (server value null), the teen is not restricted
const MODES = [
  ["nolimit", "No limit"],
  ["amount", "Monthly amount"],
  ["none", "No spending"],
];

// "$12.50" - always two decimals, so amounts line up and never show floating point noise like 12.499999.
export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Turns what the server says (monthly_amount: null | 0 | 25) into the form's mode.
export const modeOf = (monthlyAmount) => {
  if (monthlyAmount === null || monthlyAmount === undefined) return "nolimit";
  return Number(monthlyAmount) === 0 ? "none" : "amount";
};

// Turns the form back into the value the API wants: null (no limit), 0 (no spending) or a dollar amount.
// Returns { error } instead of a value when the typed amount cannot be used, so the caller can show the message under the field.
export const valueFor = (mode, typed) => {
  if (mode === "nolimit") return { value: null };
  if (mode === "none") return { value: 0 };
  const text = String(typed || "").trim();
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(text)) return { error: "Enter an amount in dollars, like 25 or 12.50." };
  const amount = Number(text);
  if (amount <= 0) return { error: "Enter an amount above $0, or choose No spending." };
  if (amount > 10000) return { error: "The allowance can be at most $10,000 a month." };
  return { value: amount };
};

// The date of a purchase as the parent expects to read it, for example "Nov 2".
const shortDate = (value) => (value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

// The API puts the reason for a refusal in response.data.message; fall back to a plain sentence when there is none.
const messageOf = (e, fallback = "Something went wrong. Please try again.") => e?.response?.data?.message || fallback;

// A thin bar showing how much of the allowance is used. It turns red when nothing is left so a parent sees it without reading numbers.
function SpendBar({ spent, allowance }) {
  const share = allowance > 0 ? Math.min(100, Math.round((spent / allowance) * 100)) : 100;
  return (
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200" role="img" aria-label={`${share}% of the allowance used`} data-testid="spend-bar">
      <div className="h-full rounded-full" style={{ width: `${share}%`, backgroundColor: share >= 100 ? "#dc2626" : ACCENT }} />
    </div>
  );
}

export default function AllowanceEditor({ childId }) {
  // `data` is the server's answer: { child, allowance: { monthly_amount }, month: { start, spent, remaining }, purchases: [...] }.
  const [data, setData] = useState(null);
  // `mode` and `typed` are the form fields; they start from the saved allowance and change as the parent edits.
  const [mode, setMode] = useState("nolimit");
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // Puts a server answer on screen and resets the form to what was saved, so the form never disagrees with the server.
  const show = (res) => {
    setData(res);
    setMode(modeOf(res.allowance.monthly_amount));
    setTyped(res.allowance.monthly_amount ? String(res.allowance.monthly_amount) : "");
  };

  // Load the allowance once when the panel opens (and again if the parent opens it for a different teen).
  // `live` stops a slow answer from writing into the page after the panel was closed.
  useEffect(() => {
    let live = true;
    parentalApi.childAllowance(childId)
      .then((res) => { if (live) show(res); })
      .catch((e) => { if (live) setError(messageOf(e, "We couldn't load the spending allowance.")); });
    return () => { live = false; };
  }, [childId]);

  // Any change to the form hides the old "Saved" message, so it never claims something that is no longer true.
  const change = (fn) => { setSaved(false); setError(""); fn(); };

  const save = async (event) => {
    event.preventDefault();
    setError(""); setSaved(false);
    // Check the typed amount here first so the parent gets an instant message instead of a round trip to the server.
    const { value, error: problem } = valueFor(mode, typed);
    if (problem) return setError(problem);
    setBusy(true);
    try {
      // The server answers with the fresh numbers (spent and remaining), so there is no need to calculate anything here.
      show(await parentalApi.saveAllowance(childId, value));
      setSaved(true);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  // Until the first answer arrives, show a short status line (or the error when the load failed).
  if (!data) return <p className="mt-3 text-sm text-gray-500" role={error ? "alert" : "status"}>{error || "Loading spending allowance…"}</p>;

  const { allowance, month, purchases } = data;
  const hasAllowance = allowance.monthly_amount !== null;
  const input = "rounded-lg border border-gray-300 px-3 py-2 text-sm";

  return (
    <form onSubmit={save} className="mt-3 rounded-xl bg-gray-50 p-4" data-testid="allowance-editor">
      <h3 className="text-sm font-bold text-gray-900">Spending allowance</h3>

      {/* 1. This month so far. Without an allowance we still show what was spent, so the parent can decide on a number. */}
      <p className="mt-1 text-sm text-gray-700" data-testid="month-spent">
        This month: <strong>{money(month.spent)}</strong> spent
        {hasAllowance ? <> of {money(allowance.monthly_amount)} · <strong>{money(month.remaining)}</strong> left</> : " (no limit set)"}
      </p>
      {hasAllowance && allowance.monthly_amount > 0 && <SpendBar spent={month.spent} allowance={allowance.monthly_amount} />}

      {/* 2. The setting. Radio buttons for the three modes; the amount box only appears for "Monthly amount". */}
      <fieldset className="mt-4">
        <legend className="text-sm font-semibold text-gray-800">Allowance</legend>
        <div className="mt-2 flex flex-wrap gap-4 text-sm">
          {MODES.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input type="radio" name={`allowance-mode-${childId}`} checked={mode === key} onChange={() => change(() => setMode(key))} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      {mode === "amount" && (
        <label className="mt-3 block text-sm font-semibold text-gray-800">Dollars a month
          <input
            type="text" inputMode="decimal" value={typed} maxLength={8} placeholder="25.00" autoComplete="off"
            // Only digits and one decimal point can be typed, so the amount can never contain letters (the music portal checks input while typing).
            onChange={(e) => change(() => setTyped(e.target.value.replace(/[^\d.]/g, "")))}
            className={`${input} mt-1 block w-40`} aria-label="Allowance in dollars per month"
          />
        </label>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}>
          {busy ? "Saving…" : "Save allowance"}
        </button>
        {saved && <span className="text-sm text-green-700" role="status">Saved. Your teen has been told.</span>}
      </div>
      {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}

      {/* 3. What the money went on this month (memberships, licences, renewals and tips; a purchase refunded in full is not listed). */}
      {purchases.length > 0 && (
        <div className="mt-4" data-testid="month-purchases">
          <p className="text-sm font-semibold text-gray-800">Paid this month</p>
          <ul className="mt-1 divide-y divide-gray-200 text-sm text-gray-700">
            {purchases.map((p, i) => (
              <li key={`${p.type}-${p.date}-${i}`} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0 truncate">{shortDate(p.date)} · {p.item}</span>
                <span className="shrink-0 font-semibold">{money(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-xs text-gray-500">
        A payment that would go over the allowance is stopped before the card is charged, and you are told. The month starts on the 1st. Automatic licence renewals are not stopped.
      </p>
    </form>
  );
}
